/**
 * Reading module state hook.
 *
 * Manages state for reading module including chunks, questions, answers,
 * timer, and UI preferences. Fetches real data from backend API.
 *
 * @module useReadingModuleState
 */

import { useReducer, useCallback, useMemo, useEffect, useState } from 'react';
import { fetchReadingModuleData } from '@/lib/examApi';
import { transformSimulationData } from '@/utils/readingDataTransform';
import { reorderSimulationData, reorderSimulationOptions } from '@/utils/reorderSimulationData';
import { useModuleStateBase } from './useModuleStateBase';
import { useModuleData } from './useModuleData';
import { useTimer } from './useTimer';
import { useSimulationDraft } from './useSimulationDraft';
import { readDraftQuestionOrder } from '@/lib/simulationDraftStorage';
import { DEFAULT_READING_TIME_SECONDS } from '@/lib/constants';
import { findChunkIndexForQuestionId } from '@/utils/simulationQuestionNavigation';
import type {
  ReadingModuleState,
  CEFRLevel,
  ExamType,
  SimulationSettings,
  TextChunk,
} from '@/types/exam-simulation';

/**
 * Options for the reading module state hook.
 */
interface UseReadingModuleStateOptions {
  /** Exam type (goethe, osd, or telc) */
  examType: ExamType;
  /** CEFR proficiency level (A1, A2, B1, B2, C1, or C2) */
  level: CEFRLevel;
  /** Simulation settings from API (null if not yet loaded) */
  settings: SimulationSettings | null;
  /** Current user ID (optional, used for session tracking) */
  userId?: string | null;
  /** Session ID passed from parent (if provided, skips local generation) */
  sessionId?: string | null;
}

/**
 * Action types for reading module state updates
 */
type ReadingModuleAction =
  | { type: 'SET_ANSWER'; questionId: number; answerId: string }
  | { type: 'TOGGLE_STRIKE'; questionId: number; answerId: string }
  | { type: 'TOGGLE_FLAG'; questionId: number }
  | { type: 'SET_ACTIVE_QUESTION'; questionId: number | null }
  | { type: 'JUMP_TO_QUESTION'; chunkIndex: number; questionId: number }
  | { type: 'NAVIGATE_CHUNK'; direction: 'prev' | 'next'; chunks: TextChunk[] }
  | { type: 'SET_TEXT_SIZE'; size: 'small' | 'medium' | 'large' }
  | { type: 'TOGGLE_HIGHLIGHTER' }
  | { type: 'ERASE_HIGHLIGHTS' }
  | { type: 'ADD_HIGHLIGHT'; paragraphId: number; start: number; end: number; column?: 'left' | 'right' }
  | { type: 'SET_TIME_REMAINING'; time: number }
  | { type: 'REHYDRATE'; payload: Partial<ReadingModuleState> };

/**
 * Merges overlapping text highlight ranges and sorts them.
 * 
 * @param ranges - Array of highlight ranges to merge
 * @returns Sorted array of merged, non-overlapping ranges
 */
function mergeHighlightRanges(ranges: { start: number; end: number }[]): { start: number; end: number }[] {
  if (ranges.length <= 1) return ranges;

  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [];

  for (const range of sorted) {
    const last = merged.at(-1);

    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }

  return merged;
}

/**
 * Reducer for reading module state
 */
function readingModuleReducer(
  state: ReadingModuleState,
  action: ReadingModuleAction,
): ReadingModuleState {
  switch (action.type) {
    case 'SET_ANSWER': {
      const previous = state.selectedAnswers[action.questionId];
      const isRevision =
        previous !== undefined && previous !== null && previous !== action.answerId;
      const changedQuestionIds =
        isRevision && !state.changedQuestionIds[action.questionId]
          ? { ...state.changedQuestionIds, [action.questionId]: true as const }
          : state.changedQuestionIds;
      return {
        ...state,
        selectedAnswers: { ...state.selectedAnswers, [action.questionId]: action.answerId },
        changedQuestionIds,
      };
    }

    case 'TOGGLE_STRIKE': {
      const current = state.struckAnswers[action.questionId] || [];
      const newStrikes = current.includes(action.answerId)
        ? current.filter((id) => id !== action.answerId)
        : [...current, action.answerId];
      return {
        ...state,
        struckAnswers: { ...state.struckAnswers, [action.questionId]: newStrikes },
      };
    }

    case 'TOGGLE_FLAG': {
      const newFlagged = state.flaggedQuestions.includes(action.questionId)
        ? state.flaggedQuestions.filter((id) => id !== action.questionId)
        : [...state.flaggedQuestions, action.questionId];
      return {
        ...state,
        flaggedQuestions: newFlagged,
      };
    }

    case 'SET_ACTIVE_QUESTION':
      return {
        ...state,
        activeQuestionId: action.questionId,
      };

    case 'JUMP_TO_QUESTION':
      return {
        ...state,
        currentChunkIndex: action.chunkIndex,
        activeQuestionId: action.questionId,
      };

    case 'NAVIGATE_CHUNK': {
      const { direction, chunks } = action;
      const isNext = direction === 'next';

      const newIndex = isNext
        ? Math.min(state.currentChunkIndex + 1, chunks.length - 1)
        : Math.max(state.currentChunkIndex - 1, 0);

      const firstQuestionId = chunks[newIndex]?.questionIds[0] || null;

      return {
        ...state,
        currentChunkIndex: newIndex,
        activeQuestionId: firstQuestionId,
      };
    }

    case 'SET_TEXT_SIZE':
      return {
        ...state,
        textSize: action.size,
      };

    case 'TOGGLE_HIGHLIGHTER':
      return {
        ...state,
        highlighterActive: !state.highlighterActive,
      };

    case 'ERASE_HIGHLIGHTS':
      return {
        ...state,
        highlights: {},
      };

    case 'ADD_HIGHLIGHT': {
      const { paragraphId, column, start, end } = action;
      const highlightKey = column ? `${paragraphId}-${column}` : String(paragraphId);

      const currentHighlights = state.highlights[highlightKey] || [];
      const merged = mergeHighlightRanges([...currentHighlights, { start, end }]);

      return {
        ...state,
        highlights: {
          ...state.highlights,
          [highlightKey]: merged,
        },
      };
    }

    case 'SET_TIME_REMAINING':
      return {
        ...state,
        timeRemaining: action.time,
      };

    case 'REHYDRATE':
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }
}

/**
 * Hook for managing reading module state and interactions.
 *
 * Provides comprehensive state management for the reading exam module including:
 * - Data fetching and transformation from backend
 * - Answer tracking and submission
 * - Timer management
 * - UI preferences (text size, highlighter)
 * - Question navigation and highlighting
 *
 * Automatically fetches reading module data when settings are available and
 * transforms it into the frontend format. Manages timer countdown and provides
 * functions for saving answers and completing the module.
 *
 * @param options - Configuration options for the hook
 * @param options.examType - Exam type identifier
 * @param options.level - CEFR proficiency level
 * @param options.settings - Simulation settings (must be provided for data fetching)
 * @param options.userId - Optional user ID for session tracking
 * @returns Object containing:
 *   - `state`: Current reading module state (chunks, questions, answers, timer, etc.)
 *   - `chunks`: Array of text chunks with paragraphs
 *   - `allQuestions`: Array of all questions
 *   - `isLoadingData`: Boolean indicating if data is being fetched
 *   - `dataError`: Error object if data fetch failed
 *   - `setState`: Function to update module state
 *   - `handleAnswerSelect`: Function to handle answer selection
 *   - `handleStrikeAnswer`: Function to strike/unstrike answer options
 *   - `handleFlagQuestion`: Function to flag/unflag questions
 *   - `handleHighlight`: Function to add/remove text highlights
 *   - `handleTextSizeChange`: Function to change text size
 *   - `handleHighlighterToggle`: Function to toggle highlighter tool
 *   - `handleChunkChange`: Function to navigate between chunks
 *   - `handleQuestionSelect`: Function to select a question
 *   - `handleFinishModule`: Function to finish and submit the module
 *
 * @example
 * ```typescript
 * const {
 *   state,
 *   chunks,
 *   allQuestions,
 *   handleAnswerSelect,
 *   handleFinishModule
 * } = useReadingModuleState({
 *   examType: 'goethe',
 *   level: 'B1',
 *   settings: simulationSettings,
 *   userId: user?.id
 * });
 * ```
 */
export function useReadingModuleState({
  examType,
  level,
  settings,
  userId,
  sessionId: externalSessionId,
}: UseReadingModuleStateOptions) {
  // Use base hook for shared functionality (session ID, answer submission, result calculation)
  const {
    sessionId,
    saveAnswersAndResult,
  } = useModuleStateBase({
    module: 'reading',
    examType,
    level,
    settings,
    userId,
    calculateCorrectness: true, // Reading module calculates correctness
    sessionId: externalSessionId,
  });

  // Resume: pin the exact question set saved in the draft so the learner sees
  // the same questions (and count) they were answering, instead of a freshly
  // re-randomized selection. Read once on mount; fresh runs have no draft and
  // fall through to the normal adaptive selection (pins undefined).
  const [pinnedQuestionIds] = useState<string[] | undefined>(() =>
    readDraftQuestionOrder(userId, sessionId, 'reading'),
  );

  // Fetch reading module data using shared hook
  const { data: simulationData, isLoading: isLoadingData, error: dataError } = useModuleData({
    module: 'reading',
    examType,
    level,
    settings,
    userId,
    pinnedQuestionIds,
    fetchFn: fetchReadingModuleData,
  });

  // Ordered question UUIDs from the original session; set from onRehydrate.
  // Empty = fresh session (no reordering applied).
  const [restoredQuestionOrder, setRestoredQuestionOrder] = useState<string[]>([]);
  // Per-question option text order from the original session; set from onRehydrate.
  // Empty object = fresh session (no option reordering applied).
  const [restoredOptionOrder, setRestoredOptionOrder] = useState<Record<string, string[]>>({});

  // Transform backend data to frontend format.
  // On resume, reorder question groups AND each question's options to match
  // the stored order so that sequential IDs 1, 2, 3… and option letters
  // A, B, C… always map to the same questions and option texts as the
  // original session, regardless of the backend's per-request shuffle.
  const { chunks, allQuestions } = useMemo(() => {
    if (!simulationData) {
      return { chunks: [], allQuestions: [] };
    }
    let data = simulationData;
    if (restoredQuestionOrder.length > 0) {
      data = reorderSimulationData(data, restoredQuestionOrder);
    }
    if (Object.keys(restoredOptionOrder).length > 0) {
      data = reorderSimulationOptions(data, restoredOptionOrder);
    }
    return transformSimulationData(data);
  }, [simulationData, restoredQuestionOrder, restoredOptionOrder]);

  // Get initial time from settings or use default
  const initialTime = useMemo(() => {
    if (settings?.moduleTimings?.reading) {
      return settings.moduleTimings.reading * 60; // Convert minutes to seconds
    }
    return DEFAULT_READING_TIME_SECONDS;
  }, [settings]);

  /**
   * When rehydrating from a draft with `remainingAtSave`, use that as the
   * countdown budget so time does not elapse while the learner is outside the simulation.
   */
  const [resumedBudgetSeconds, setResumedBudgetSeconds] = useState<number | null>(null);
  const effectiveInitialTime = resumedBudgetSeconds ?? initialTime;

  /**
   * Wall-clock anchor for the reading timer. Set once on first mount unless
   * rehydrate restores a legacy anchor or a fresh anchor after pause-aware resume.
   */
  const [timerStartedAt, setTimerStartedAt] = useState<number | null>(null);
  useEffect(() => {
    if (timerStartedAt === null) {
      setTimerStartedAt(Date.now());
    }
  }, [timerStartedAt]);

  const { timeRemaining, reset: resetTimer } = useTimer({
    initialTime: effectiveInitialTime,
    enabled: true,
    autoStart: true,
    startedAt: timerStartedAt,
  });

  // Initial state
  const initialState: ReadingModuleState = useMemo(
    () => ({
      currentChunkIndex: 0,
      activeQuestionId: null,
      selectedAnswers: {},
      struckAnswers: {},
      flaggedQuestions: [],
      changedQuestionIds: {},
      highlights: {},
      timeRemaining: initialTime,
      textSize: 'medium',
      highlighterActive: false,
    }),
    [initialTime],
  );

  const [state, dispatch] = useReducer(readingModuleReducer, initialState);

  // --- Draft persistence ----------------------------------------------------
  const draftPayload = useMemo(
    () => ({
      currentChunkIndex: state.currentChunkIndex,
      activeQuestionId: state.activeQuestionId,
      selectedAnswers: state.selectedAnswers,
      struckAnswers: state.struckAnswers,
      flaggedQuestions: state.flaggedQuestions,
      changedQuestionIds: state.changedQuestionIds,
      highlights: state.highlights,
      textSize: state.textSize,
      highlighterActive: state.highlighterActive,
      // Stable ordering: persisted so resume can restore the exact question
      // order even when the backend reshuffles groups on the next fetch.
      questionOrder: allQuestions.map((q) => q.originalId),
      // Per-question option text order, so on resume the letter labels A/B/C
      // point to the same option texts the learner saw before the interruption
      // — independent of backend option ordering on the next fetch.
      optionOrder: Object.fromEntries(
        allQuestions.map((q) => [q.originalId, q.options.map((o) => o.text)]),
      ),
    }),
    [state, allQuestions],
  );

  const hasUnsavedChanges = useMemo(
    () =>
      Object.keys(state.selectedAnswers).length > 0 ||
      state.flaggedQuestions.length > 0 ||
      Object.keys(state.highlights).length > 0 ||
      Object.keys(state.struckAnswers).length > 0,
    [state.selectedAnswers, state.flaggedQuestions, state.highlights, state.struckAnswers],
  );

  const draft = useSimulationDraft({
    userId,
    sessionId,
    module: 'reading',
    step: 'module',
    examType,
    level,
    timer:
      timerStartedAt === null
        ? null
        : {
            initialTimeSeconds: initialTime,
            startedAt: timerStartedAt,
            remainingAtSave: timeRemaining,
          },
    payload: draftPayload,
    shouldPersist: () => hasUnsavedChanges,
    onRehydrate: (envelope) => {
      const payload = envelope.payload as Partial<ReadingModuleState> & {
        questionOrder?: string[];
        optionOrder?: Record<string, string[]>;
      };
      // Restore question order so the useMemo reorders fresh API data before
      // sequential IDs are assigned, keeping savedAnswers keys consistent.
      if (Array.isArray(payload.questionOrder) && payload.questionOrder.length > 0) {
        setRestoredQuestionOrder(payload.questionOrder);
      }
      // Restore option order so option letters A/B/C map to the same texts
      // the learner saw before the interruption.
      if (payload.optionOrder && typeof payload.optionOrder === 'object') {
        setRestoredOptionOrder(payload.optionOrder);
      }
      dispatch({ type: 'REHYDRATE', payload });
      if (envelope.timer) {
        if (envelope.timer.remainingAtSave == null) {
          setResumedBudgetSeconds(null);
          setTimerStartedAt(envelope.timer.startedAt);
        } else {
          setResumedBudgetSeconds(envelope.timer.remainingAtSave);
          setTimerStartedAt(Date.now());
        }
      }
    },
  });

  // Sync timer with state - update state when timer changes
  useEffect(() => {
    if (timeRemaining !== state.timeRemaining) {
      dispatch({ type: 'SET_TIME_REMAINING', time: timeRemaining });
    }
  }, [timeRemaining, state.timeRemaining]);

  // Reset timer when initialTime changes (but only if we don't have an anchor yet)
  useEffect(() => {
    if (timerStartedAt === null) {
      resetTimer(effectiveInitialTime);
    }
  }, [effectiveInitialTime, resetTimer, timerStartedAt]);

  const setActiveQuestion = useCallback((questionId: number | null) => {
    dispatch({ type: 'SET_ACTIVE_QUESTION', questionId });
  }, []);

  const jumpToQuestion = useCallback(
    (questionId: number) => {
      const chunkIndex = findChunkIndexForQuestionId(chunks, questionId);
      if (chunkIndex === -1) {
        return;
      }
      dispatch({ type: 'JUMP_TO_QUESTION', chunkIndex, questionId });
    },
    [chunks],
  );

  const selectAnswer = useCallback(
    (questionId: number, answerId: string) => {
      dispatch({ type: 'SET_ANSWER', questionId, answerId });
      draft.requestSaveIndicator();
    },
    [draft],
  );

  const toggleStrikeAnswer = useCallback((questionId: number, answerId: string) => {
    dispatch({ type: 'TOGGLE_STRIKE', questionId, answerId });
  }, []);

  const toggleFlagQuestion = useCallback((questionId: number) => {
    dispatch({ type: 'TOGGLE_FLAG', questionId });
  }, []);

  const navigateChunk = useCallback(
    (direction: 'prev' | 'next') => {
      dispatch({ type: 'NAVIGATE_CHUNK', direction, chunks });
    },
    [chunks],
  );

  const setTextSize = useCallback((size: 'small' | 'medium' | 'large') => {
    dispatch({ type: 'SET_TEXT_SIZE', size });
  }, []);

  const toggleHighlighter = useCallback(() => {
    dispatch({ type: 'TOGGLE_HIGHLIGHTER' });
  }, []);

  const eraseHighlights = useCallback(() => {
    dispatch({ type: 'ERASE_HIGHLIGHTS' });
  }, []);

  const addHighlight = useCallback((paragraphId: number, start: number, end: number, column?: 'left' | 'right') => {
    // Ignore empty selections
    if (start >= end) {
      return;
    }
    dispatch({ type: 'ADD_HIGHLIGHT', paragraphId, start, end, column });
  }, []);

  /**
   * Wrap the base submission helper to clear the local draft on success.
   * Failures keep the draft intact so the learner can retry without losing
   * their answers.
   */
  const saveAnswersAndResultWithDraftClear = useCallback(
    async (questions: Parameters<typeof saveAnswersAndResult>[0], selectedAnswers: Parameters<typeof saveAnswersAndResult>[1]) => {
      const result = await saveAnswersAndResult(questions, selectedAnswers, state.changedQuestionIds);
      draft.clear();
      return result;
    },
    [saveAnswersAndResult, draft, state.changedQuestionIds],
  );

  const currentChunk = chunks[state.currentChunkIndex] || chunks[0] || null;
  const currentQuestions = useMemo(() => {
    if (!currentChunk) {
      return [];
    }
    return allQuestions.filter((q) => currentChunk.questionIds.includes(q.id));
  }, [currentChunk, allQuestions]);

  // Get the paragraph ID that should be highlighted based on active question
  const highlightedParagraphId = useMemo(() => {
    if (!state.activeQuestionId) {
      return null;
    }
    return allQuestions.find((q) => q.id === state.activeQuestionId)?.paragraphRef || null;
  }, [state.activeQuestionId, allQuestions]);

  // Create state with synced timeRemaining from timer
  const stateWithTimer = useMemo(
    () => ({
      ...state,
      timeRemaining,
    }),
    [state, timeRemaining],
  );

  return {
    state: stateWithTimer,
    currentChunk,
    currentQuestions,
    highlightedParagraphId,
    totalChunks: chunks.length,
    totalQuestions: allQuestions.length,
    allQuestions,
    isLoadingData,
    dataError,
    sessionId,
    setActiveQuestion,
    jumpToQuestion,
    selectAnswer,
    toggleStrikeAnswer,
    toggleFlagQuestion,
    navigateChunk,
    setTextSize,
    toggleHighlighter,
    eraseHighlights,
    addHighlight,
    saveAnswersAndResult: saveAnswersAndResultWithDraftClear,
  };
}

